import isFinite from 'lodash/isFinite'
import isInteger from 'lodash/isInteger'

import { Money } from '@klear/money'
import { Rate } from '@klear/rate'

export const MAX_BASIS_POINTS = 10000 as const

export type BasisPointsFormat = 'bps' | 'percent'

export interface BasisPointsFormatOptions {
  format?: BasisPointsFormat
  digits?: number
}

export interface BasisPointsOptions {
  allowNegative?: boolean
}

function assertValidBps(value: number, { allowNegative = false }: BasisPointsOptions = {}): void {
  const min = allowNegative ? -MAX_BASIS_POINTS : 0
  if (!isInteger(value) || !isFinite(value) || value < min || value > MAX_BASIS_POINTS) {
    throw new Error(`BasisPoints must be an integer between ${min} and ${MAX_BASIS_POINTS}: got ${value}`)
  }
}

export function basisPointsToJson(basisPoints: BasisPoints): number {
  return basisPoints.value
}
export type BasisPointsJson = ReturnType<typeof basisPointsToJson>

function trimTrailingZeros(text: string): string {
  if (!text.includes('.')) return text
  return text.replace(/\.?0+$/, '')
}

export class BasisPoints {
  static readonly __klearClass = 'BasisPoints'
  static readonly MAX = MAX_BASIS_POINTS
  static readonly ZERO = new BasisPoints(0)
  static readonly DEFAULT_ADVANCE_RATE = new BasisPoints(9000)

  readonly value: number

  constructor(value: BasisPointsJson, opts: BasisPointsOptions = {}) {
    assertValidBps(value, opts)
    this.value = value
  }

  static signed(value: number): BasisPoints {
    return new BasisPoints(value, { allowNegative: true })
  }

  toRate(): Rate {
    return Rate.of(BigInt(this.value), BigInt(MAX_BASIS_POINTS))
  }

  applyTo(money: Money): Money {
    return this.toRate().applyTo(money)
  }

  annualizedFee(money: Money, days: number, daysInYear: number = 360): Money {
    if (!isInteger(days) || !isFinite(days) || days < 0) {
      throw new Error(`Annualized fee needs a non-negative integer number of days: got ${days}`)
    }
    if (!isInteger(daysInYear) || daysInYear <= 0) {
      throw new Error(`Annualized fee needs a positive integer year length: got ${daysInYear}`)
    }
    return this.toRate().scaled(BigInt(days), BigInt(daysInYear)).applyTo(money)
  }

  static fromMoney(portion: Money, whole: Money): BasisPoints {
    return BasisPoints.ratioFromMoney(portion, whole, { allowNegative: false })
  }

  static signedFromMoney(portion: Money, whole: Money): BasisPoints {
    return BasisPoints.ratioFromMoney(portion, whole, { allowNegative: true })
  }

  private static ratioFromMoney(
    portion: Money,
    whole: Money,
    { allowNegative }: { allowNegative: boolean }
  ): BasisPoints {
    if (portion.currency !== whole.currency && !portion.isZero() && !whole.isZero()) {
      console.warn(
        `BasisPoints.ratioFromMoney: currency mismatch ${portion.currency} vs ${whole.currency}; returning 0`
      )
      return BasisPoints.ZERO
    }
    if (whole.amount <= 0n) return BasisPoints.ZERO

    const maxBps = BigInt(MAX_BASIS_POINTS)
    const bps = (portion.amount * maxBps + whole.amount / 2n) / whole.amount
    let clamped = bps
    if (allowNegative) {
      if (clamped < -maxBps) clamped = -maxBps
      else if (clamped > maxBps) clamped = maxBps
    } else {
      if (clamped < 0n) clamped = 0n
      else if (clamped > maxBps) clamped = maxBps
    }

    const value = parseInt(clamped.toString(), 10)
    if (allowNegative) return BasisPoints.signed(value)
    return new BasisPoints(value)
  }

  toString(options: BasisPointsFormatOptions = {}): string {
    const { format = 'percent', digits } = options
    if (format === 'bps') return `${this.value} bps`

    return `${trimTrailingZeros(this.toPercentString(digits ?? 2))}%`
  }

  toPercentString(digits: number = 2): string {
    return this.toRate().toPercentString(digits)
  }

  valueOf(): number {
    return this.value
  }

  toJSON(): BasisPointsJson {
    return basisPointsToJson(this)
  }
}
